import * as React from 'react'

import { Button } from '../actions/button'
import { VoleeLockup } from '../identity/volee-lockup'
import { cn } from '../lib/cn'
import { IconPause, IconPlay } from '../lib/icons'
import { REDUCED_MOTION, useMediaQuery } from '../lib/use-media-query'
import { usePageVisible } from '../lib/use-page-visible'
import { EntryBackdrop, type EntryMotion } from './entry-backdrop'
import { EntrySheet } from './entry-sheet'
import { ENTRY_HEADLINE_TYPE } from '../typography/typography'

// The door of the product: a full-screen, dark frame that other screens open a sheet on. It knows
// neither the router nor Cognito (packages/ui/docs/cornice-d-ingresso.md). Labels are props.

/** What the picture does now: nothing for who asked for less motion, held while paused or hidden. */
function useEntryMotion() {
  const reduced = useMediaQuery(REDUCED_MOTION)
  const visible = usePageVisible()
  const [paused, setPaused] = React.useState(false)
  let motion: EntryMotion = 'running'
  if (reduced) motion = 'still'
  else if (paused || !visible) motion = 'paused'
  return { motion, paused, togglePause: () => setPaused((was) => !was) }
}

export interface EntryHeadline {
  /** The first sentence, in the text colour. */
  lead: string
  /** The second sentence, in lime. */
  accent: string
}

export interface EntryFrameProps {
  /** The product's name, already translated: the lockup's accessible name. */
  label: string
  /** What the pause button says while the motion runs. */
  pauseLabel: string
  /** What it says while the motion is held. */
  resumeLabel: string
  /** The sheet's close button. */
  closeLabel: string
  /** The manifesto, in the page's `h1`. */
  headline: EntryHeadline
  /** What the door offers: the router's links, as buttons. Set aside while the sheet is open. */
  actions: React.ReactNode
  /** Whether the sheet stands over the picture. The app decides: the frame never navigates. */
  open: boolean
  /** The sheet asked to go: the close button, Esc, the scrim. */
  onClose: () => void
  /** The exit finished; route owners may now remove the form. */
  onClosed?: () => void
  /** What replaces the growing photo; the lights and the veil stay over it. */
  background?: React.ReactNode
  /** Names the view the sheet shows, once the app has more than one: when it changes the new view
   *  fades in and its title takes the focus. */
  viewKey?: string
  /** The sheet's content, headed by an `EntrySheetTitle`. */
  children?: React.ReactNode
}

export function EntryFrame({
  label,
  pauseLabel,
  resumeLabel,
  closeLabel,
  headline,
  actions,
  open,
  onClose,
  onClosed,
  background,
  viewKey,
  children,
}: EntryFrameProps) {
  const { motion, paused, togglePause } = useEntryMotion()
  const [opening] = React.useState(motion !== 'still')
  const actionsRef = React.useRef<HTMLDivElement>(null)
  return (
    <div
      data-surface="ink"
      data-motion={motion}
      className="fixed inset-0 isolate overflow-hidden bg-background"
    >
      <EntryBackdrop motion={motion} opening={opening} background={background} />
      <div className="absolute inset-0 overflow-y-auto pt-safe-top pr-safe-right pb-safe-bottom pl-safe-left">
        <div className="relative flex min-h-full flex-col">
          <header className="absolute inset-x-0 top-0 flex items-center justify-between px-(--spacing-content-gutter) py-3.5 entry:px-8 entry:py-6">
            <VoleeLockup
              label={label}
              opening={opening}
              className="entry:h-(--size-brand-lockup-wide)"
            />
            {motion === 'still' ? null : (
              <Button
                variant="glass"
                size="icon"
                className="size-(--size-control-lg)"
                aria-label={paused ? resumeLabel : pauseLabel}
                onClick={togglePause}
              >
                {paused ? <IconPlay aria-hidden="true" /> : <IconPause aria-hidden="true" />}
              </Button>
            )}
          </header>
          <main className="flex flex-1 flex-col items-center justify-between gap-8 px-(--spacing-content-gutter) pt-(--spacing-entry-headline-top) pb-8 entry:px-(--spacing-content-gutter-lg) entry:pt-(--spacing-entry-headline-top-wide)">
            <h1 className={cn(ENTRY_HEADLINE_TYPE, 'text-center text-balance text-shadow-entry')}>
              <span className={cn('block', opening && 'animate-entry-headline-lead')}>
                {headline.lead}
              </span>{' '}
              <span
                className={cn(
                  'block text-primary-text',
                  opening && 'animate-entry-headline-accent',
                )}
              >
                {headline.accent}
              </span>
            </h1>
            <div
              ref={actionsRef}
              data-sheet={open ? 'open' : 'closed'}
              className={cn(
                'flex w-full max-w-(--size-entry-column) flex-col gap-2 data-[sheet=open]:opacity-0',
                motion !== 'still' && 'transition-opacity duration-(--duration-entry-actions)',
                opening && 'animate-entry-actions',
              )}
            >
              {actions}
            </div>
          </main>
        </div>
      </div>
      <EntrySheet
        open={open}
        onClose={onClose}
        onClosed={onClosed}
        closeLabel={closeLabel}
        label={label}
        animated={motion !== 'still'}
        viewKey={viewKey}
        returnTo={actionsRef}
      >
        {children}
      </EntrySheet>
    </div>
  )
}
